import React, { useMemo } from 'react';
import { 
  ArrowUpDown
} from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ComposedChart, Area, AreaChart, Cell
} from 'recharts';

/**
 * memoized chart components to prevent redundant layout cycles and ensure smooth interactions.
 */

// 1. TRENDS CHART
export const TrendsChart = React.memo(({ data, darkMode, trendViewMode, facilityData, selectedFacility, visibleFacilities, siteColors, isActiveSite }) => (
  <ResponsiveContainer width="100%" height="100%">
    <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#374151" : "#E5E7EB"} />
      <XAxis dataKey="month" stroke={darkMode ? "#9CA3AF" : "#6B7280"} />
      <YAxis yAxisId="left" stroke={darkMode ? "#9CA3AF" : "#6B7280"} label={{ value: 'Performance Metric', angle: -90, position: 'insideLeft', fill: darkMode ? '#9CA3AF' : '#6B7280', offset: -5 }}/>
      <YAxis yAxisId="right" orientation="right" stroke={darkMode ? "#9CA3AF" : "#6B7280"} label={{ value: 'Total Cost ($)', angle: 90, position: 'insideRight', fill: darkMode ? '#9CA3AF' : '#6B7280', offset: 5 }}/>
      <Tooltip 
        offset={25}
        contentStyle={{ 
          backgroundColor: darkMode ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)', 
          borderColor: darkMode ? '#374151' : '#E5E7EB', 
          borderRadius: '8px', 
          border: 'none', 
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(8px)'
        }}
        itemStyle={{ color: darkMode ? '#E5E7EB' : '#374151', fontSize: '12px' }}
        labelStyle={{ color: darkMode ? '#F3F4F6' : '#111827', fontWeight: 'bold', marginBottom: '8px' }}
        formatter={(value, name) => {
          if (typeof value !== 'number') return [value, name];
          const isCurrency = name.toLowerCase().includes('cost');
          const formatted = isCurrency
            ? `$${value.toLocaleString(undefined, {maximumFractionDigits: 0})}` 
            : value.toLocaleString(undefined, {maximumFractionDigits: 0});
          return [formatted, name];
        }}
      />
      <Legend />
      
      {trendViewMode === 'aggregated' ? (
        <>
          <Bar yAxisId="left" dataKey="total_elec_kwh" name="Selected Electric (kWh)" fill="#3B82F6" stackId="usage" isAnimationActive={false} />
          <Bar yAxisId="left" dataKey="total_gas_ccf" name="Selected Gas (CCF)" fill="#F59E0B" stackId="usage" isAnimationActive={false} />
          <Line yAxisId="right" type="monotone" dataKey="total_cost" name="Selected Total Cost ($)" stroke="#10B981" strokeWidth={4} dot={{ r: 4 }} isAnimationActive={false} />
        </>
      ) : (
        <>
          {Object.values(facilityData)
            .filter(f => (selectedFacility === 'all' ? visibleFacilities.includes(f.name) : f.name === selectedFacility))
            .map((f, idx) => (
            <Line 
              key={f.name}  yAxisId="right"  type="monotone"  dataKey={`${f.name}_cost`} 
              name={`${f.name} Cost`}  stroke={siteColors[idx % siteColors.length]} 
              strokeWidth={isActiveSite(f.name) ? 4 : 1} strokeOpacity={isActiveSite(f.name) ? 1 : 0.4}
              dot={false} activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          ))}
          {selectedFacility === 'all' && (
            <Line yAxisId="right" type="monotone" dataKey="total_cost" name="PORTFOLIO TOTAL" stroke="#10B981" strokeWidth={4} strokeDasharray="10 10" dot={false} isAnimationActive={false} />
          )}
        </>
      )}
    </ComposedChart>
  </ResponsiveContainer>
));

// 2. BREAKDOWN CHART (Stacked Cost by Facility)
export const BreakdownChart = React.memo(({ data, darkMode, visibleFacilities, facilityNames, siteColors }) => {
  // 1. Fixed color mapping to ensure a site consistently has the same color
  const siteColorMap = useMemo(() => {
    const map = {};
    facilityNames.forEach((f, i) => {
      map[f.name] = siteColors[i % siteColors.length];
    });
    return map;
  }, [facilityNames, siteColors]);

  // 2. Data transformation: for each month, rank sites by their cost contribution for the stack
  const transformedData = useMemo(() => {
    return data.map(month => {
      const entry = { ...month };
      // Gather only visible facilities for sorting
      const siteEntries = visibleFacilities.map(name => ({
        name,
        cost: month[`${name}_cost`] || 0
      }));
      
      // Sort smallest to largest (Recharts stacks in order of <Bar> definition)
      siteEntries.sort((a, b) => a.cost - b.cost);
      
      // Map to generic rank keys for the <Bar> components
      siteEntries.forEach((s, i) => {
        entry[`rank${i}_val`] = s.cost;
        entry[`rank${i}_name`] = s.name;
      });
      
      return entry;
    });
  }, [data, visibleFacilities]);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={transformedData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#374151" : "#E5E7EB"} />
        <XAxis dataKey="month" stroke={darkMode ? "#9CA3AF" : "#6B7280"} />
        <YAxis stroke={darkMode ? "#9CA3AF" : "#6B7280"} label={{ value: 'Cost Contribution ($)', angle: -90, position: 'insideLeft', fill: darkMode ? '#9CA3AF' : '#6B7280', offset: -5 }} />
        <Tooltip 
          contentStyle={{ 
            backgroundColor: darkMode ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)', 
            borderColor: darkMode ? '#374151' : '#E5E7EB',
            borderRadius: '8px',
            border: 'none',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(8px)'
          }}
          itemStyle={{ color: darkMode ? '#E5E7EB' : '#374151', fontSize: '12px' }}
          labelStyle={{ color: darkMode ? '#F3F4F6' : '#111827', fontWeight: 'bold', marginBottom: '8px' }}
          formatter={(value, name, props) => {
            const dataPoint = props.payload;
            const rankKey = props.dataKey.replace('_val', '');
            const actualName = dataPoint[`${rankKey}_name`];
            return [`$${value.toLocaleString()}`, actualName];
          }}
          itemSorter={(item) => -item.value} 
        />
        <Legend 
          content={() => (
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4">
              {facilityNames.filter(f => visibleFacilities.includes(f.name)).map((f) => (
                <div key={f.name} className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: siteColorMap[f.name] }} />
                  <span className={`text-[10px] font-bold uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{f.name}</span>
                </div>
              ))}
            </div>
          )}
        />
        {Array.from({ length: visibleFacilities.length }).map((_, i) => (
          <Bar key={i} dataKey={`rank${i}_val`} stackId="sites" isAnimationActive={false}>
            {transformedData.map((entry, index) => (
              <Cell key={`cell-${i}-${index}`} fill={siteColorMap[entry[`rank${i}_name`]]} />
            ))}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
});

// 3. PROJECTIONS CHART
export const ProjectionsChart = React.memo(({ data, darkMode }) => (
  <ResponsiveContainer width="100%" height="100%">
    <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#374151" : "#E5E7EB"} />
      <XAxis dataKey="month" stroke={darkMode ? "#9CA3AF" : "#6B7280"} />
      <YAxis yAxisId="left" stroke={darkMode ? "#9CA3AF" : "#6B7280"} label={{ value: 'Cost ($)', angle: -90, position: 'insideLeft', fill: darkMode ? '#9CA3AF' : '#6B7280', offset: -5 }}/>
      <YAxis yAxisId="right" orientation="right" stroke={darkMode ? "#9CA3AF" : "#6B7280"} label={{ value: 'Carbon (mt)', angle: 90, position: 'insideRight', fill: darkMode ? '#9CA3AF' : '#6B7280', offset: 5 }}/>
      <Tooltip 
        offset={25}
        contentStyle={{ backgroundColor: 'transparent', border: 'none' }}
        content={({ active, payload, label }) => {
          if (!active || !payload || !payload.length) return null;
          const d = payload[0].payload;
          const isHistory = d.isHistory;
          
          return (
            <div className={`p-4 border ${darkMode ? 'border-gray-700 bg-gray-800/95' : 'border-gray-200 bg-white/95'} rounded-xl shadow-2xl backdrop-blur-md min-w-[200px]`}>
              <div className={`flex justify-between items-center mb-2 border-b pb-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <span className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{label}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${isHistory ? (darkMode ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-800') : (darkMode ? 'bg-indigo-900/40 text-indigo-400' : 'bg-indigo-100 text-indigo-800')}`}>
                  {isHistory ? 'ACTUAL' : 'FORECAST'}
                </span>
              </div>
              
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between gap-6">
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{isHistory ? 'Actual Cost' : 'Forecast Cost'}:</span>
                  <span className={`font-mono font-bold ${isHistory ? 'text-green-600' : 'text-indigo-500'}`}>
                    ${(isHistory ? d.total_cost : d.forecast_cost)?.toLocaleString()}
                  </span>
                </div>

                {!isHistory && (
                  <div className="flex justify-between gap-6">
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Est. Savings:</span>
                    <span className="font-mono font-bold text-emerald-500">${(d.potential_savings || 0).toLocaleString()}</span>
                  </div>
                )}

                {d.target_cost && (
                  <div className="pt-1 mt-1 border-t dark:border-gray-700">
                    <div className="flex justify-between gap-6">
                      <span className="text-gray-500">Target Ceiling:</span>
                      <span className="font-mono text-red-500">${d.target_cost?.toLocaleString()}</span>
                    </div>
                    {isHistory && (
                      <div className={`flex justify-between gap-6 mt-1 font-bold ${d.target_status === 'HIT' ? 'text-green-500' : 'text-red-500'}`}>
                        <span className="text-[10px]">STATUS:</span>
                        <span className="text-[10px]">{d.target_status === 'HIT' ? '🏆 HIT' : '❌ OVER'}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-between gap-6 pt-2 text-xs opacity-75 border-t dark:border-gray-700">
                  <span className="text-gray-500">Carbon Intensity:</span>
                  <span className="font-mono">{(isHistory ? d.carbon_emissions : d.forecast_carbon)?.toFixed(2)} mt CO2e</span>
                </div>
              </div>
            </div>
          );
        }}
      />
      <Legend verticalAlign="bottom" height={36}/>
      <Line yAxisId="left" type="monotone" dataKey="total_cost" name="Historical Cost ($)" stroke="#9CA3AF" strokeWidth={2} strokeDasharray="5 5" isAnimationActive={false} />
      <Area yAxisId="right" type="monotone" dataKey="carbon_emissions" name="Historical Carbon (mt)" fill="#D1D5DB" stroke="#9CA3AF" fillOpacity={0.3} isAnimationActive={false} />
      <Line yAxisId="left" type="monotone" dataKey="forecast_cost" name="Forecast Cost ($)" stroke="#3B82F6" strokeWidth={3} isAnimationActive={false} />
      <Area yAxisId="right" type="monotone" dataKey="forecast_carbon" name="Forecast Carbon (mt)" fill="#8B5CF6" stroke="#8B5CF6" fillOpacity={0.3} isAnimationActive={false} />
      
      <Line yAxisId="left" type="monotone" dataKey="target_cost" name="Target Cost Limit ($)" stroke="#EF4444" strokeWidth={3} strokeDasharray="3 3" dot={false} isAnimationActive={false} />
      <Line yAxisId="right" type="monotone" dataKey="target_carbon" name="Target Carbon Limit (mt)" stroke="#B91C1C" strokeWidth={3} strokeDasharray="3 3" dot={false} isAnimationActive={false} />
    </ComposedChart>
  </ResponsiveContainer>
));

// 4. CARBON CHART
export const CarbonChart = React.memo(({ data, darkMode }) => (
  <ResponsiveContainer width="100%" height="100%">
    <AreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#374151" : "#E5E7EB"} />
      <XAxis dataKey="month" stroke={darkMode ? "#9CA3AF" : "#6B7280"} />
      <YAxis stroke={darkMode ? "#9CA3AF" : "#6B7280"} label={{ value: 'Emissions (metric tons)', angle: -90, position: 'insideLeft', fill: darkMode ? '#9CA3AF' : '#6B7280', offset: -5 }} />
      <Tooltip 
        contentStyle={{ 
          backgroundColor: darkMode ? 'rgba(31, 41, 45, 0.95)' : 'rgba(255, 255, 255, 0.95)', 
          borderColor: darkMode ? '#374151' : '#E5E7EB',
          borderRadius: '8px' 
        }}
        itemStyle={{ color: darkMode ? '#E5E7EB' : '#374151', fontSize: '12px' }}
        labelStyle={{ color: darkMode ? '#F3F4F6' : '#111827', fontWeight: 'bold' }}
        formatter={(value) => [`${value.toLocaleString(undefined, {maximumFractionDigits: 1})} mt CO2e`, 'Carbon Emissions']}
      />
      <Area type="monotone" dataKey="carbon_emissions" name="Carbon Emissions" stroke="#8B5CF6" strokeWidth={2} fillOpacity={1} fill="url(#colorCarbon)" isAnimationActive={false} />
      <defs>
        <linearGradient id="colorCarbon" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8}/>
          <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
        </linearGradient>
      </defs>
    </AreaChart>
  </ResponsiveContainer>
));
